import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import fetch from 'unfetch';
import { feature as topo2geo, merge as topoMerge } from 'topojson-client';
import booleanPointInPolygon from '@turf/boolean-point-in-polygon';

import { scrollMapIntoViewport } from '~/core/scripts/map-components/utils';
import BasisMap from '~/core/scripts/map-components/BasisMap';
import Overlay from '~/core/scripts/map-components/OverlayLayer';
import GeocoderMarker from '~/core/scripts/map-components/GeocoderMarker';

import { is768Query } from '~/core/scripts/mediaquery-helper';
import Geocoder from '~/core/scripts/components/Geocoder';
import Tooltip from '~/core/scripts/components/Tooltip';
import CustomLegend from '~/scripts/components/CustomLegend';

import MarkerLayer from './MarkerLayer';

function mergeTopojsonByKey(topoJsonData, propertyKey) {
  const key = Object.keys(topoJsonData.objects)[0];
  const topoJsonObject = topoJsonData.objects[key];
  const groups = topoJsonObject.geometries
    .reduce((res, item) => {
      const currentValue = item.properties[propertyKey];

      if (!res.includes(currentValue)) {
        res.push(currentValue);
      }

      return res;
    }, [])
    .map((value) =>
      topoMerge(
        topoJsonData,
        topoJsonObject.geometries.filter((d) => d.properties[propertyKey] === value)
      )
    );

  // const groupedTopojson = {
  //   ...topoJsonData,
  //   objects: {
  //     geo: groups
  //   }
  // };

  return groups;
}

function propError(propName, componentName) {
  return new Error(
    `Invalid prop ${propName} supplied to ${componentName}.
    You need to pass choroplethSource OR choroplethData.`
  );
}

class MapWrapper extends PureComponent {
  static propTypes = {
    choroplethSource: (props, propName, componentName) => {
      if (!props.choroplethData) {
        if (typeof props[propName] !== 'string') {
          propError(propName, componentName);
        }
      }
    },
    choroplethData: (props, propName, componentName) => {
      if (!props.choroplethSource) {
        if (typeof props[propName] !== 'object') {
          propError(propName, componentName);
        }
      }
    },
    colorKey: PropTypes.string,
    valueKey: PropTypes.string,
    showCustomLegend: PropTypes.bool,
    customLegendScale: PropTypes.func,
    showMapToggleButton: PropTypes.bool,
    zoomDelta: PropTypes.number,
    zoomSnap: PropTypes.number,
    zoomAnimation: PropTypes.bool,
    tileSize: PropTypes.number,
    zoomOffset: PropTypes.number,
    isFullScreen: PropTypes.bool,
    searchPresets: PropTypes.array,
    searchPresetsTitle: PropTypes.string,
    smoothFactor: PropTypes.number,
    fitBounds: PropTypes.arrayOf(PropTypes.array),
    maxBounds: PropTypes.arrayOf(PropTypes.array),
    geocoderCenter: PropTypes.arrayOf(PropTypes.number),
    geocoderRadius: PropTypes.number,
    enableScroll: PropTypes.bool,
  };

  static defaultProps = {
    colorKey: null,
    valueKey: null,
    colorSteps: null,
    showGeocoder: false,
    showLegend: false,
    legendPortalSelector: null,
    showMapToggleButton: false,
    zoomDelta: 1,
    zoomSnap: 0.2,
    zoomAnimation: true,
    tileSize: 256,
    zoomOffset: 0,
    isFullScreen: false,
    searchPresets: [],
    smoothFactor: 0.5,
    fitBounds: null,
    maxBounds: null,
    geocoderCenter: null,
    geocoderRadius: 25000,
    enableScroll: true,
    showRecoveredCases: true,
  };

  state = {
    data: null,
    mapBounds: null,
    tooltipPos: { x: 45, y: 45 },
    tooltipData: null,
    tooltipVisible: false,
    geocoderLatLng: [],
    overlayGeoJson: null,
    dissolvedOverlayGeoJson: null,
    maxBounds: null,
  };

  componentDidMount() {
    this.loadOverlay();
  }

  loadOverlay = async () => {
    if (!this.props.overlaySource) {
      return false;
    }

    const topojson = await this.getTopojson(this.props.overlaySource);
    const key = Object.keys(topojson.objects)[0];
    const data = topo2geo(topojson, topojson.objects[key]);
    const mapBounds = L.geoJSON(data).getBounds();
    const maxBounds = mapBounds.pad(0.2);

    const dissolvedOverlayGeoJson = this.props.overlayDissolveKey
      ? mergeTopojsonByKey(topojson, this.props.overlayDissolveKey)
      : null;
    this.setState({ overlayGeoJson: data, dissolvedOverlayGeoJson, mapBounds, maxBounds });
  };

  getTopojson = async (source) => {
    const response = await fetch(source);
    const topojson = await response.json();
    return topojson;
  };

  onCloseTooltip = () => {
    this.setState({ tooltipVisible: false, tooltipData: null });
  };

  onGeocoderSuccess = (res) => {
    const latlng = res.response.view[0].result[0].location.displayPosition;
    this.setState({
      geocoderLatLng: [
        latlng.latitude + Math.random() / 10000,
        latlng.longitude + Math.random() / 10000,
      ],
    });

    if (!is768Query()) {
      scrollMapIntoViewport();

      // for mobile devices we want to show the tooltip
      let matchingFeature = null;

      for (let i = 0; i < this.state.data.features.length; i++) {
        const currFeat = this.state.data.features[i];
        const isInside =
          currFeat.type &&
          currFeat.geometry &&
          booleanPointInPolygon([latlng.longitude, latlng.latitude], currFeat);
        if (isInside) {
          matchingFeature = currFeat;
          break;
        }
      }

      this.onMouseEnter(matchingFeature.id, matchingFeature.properties);
    }
  };

  onMouseEnter = (tooltipData) => {
    this.setState({
      tooltipVisible: true,
      tooltipData,
    });
  };

  onMouseMove = (tooltipPos) => {
    this.setState({ tooltipPos });
  };

  onMouseOut = () => {
    this.setState({ tooltipVisible: false, tooltipData: null });
  };

  render() {
    const hasOverlay = !!this.props.overlaySource;
    const renderMarkers = !hasOverlay || (hasOverlay && !!this.state.overlayGeoJson);
    const renderOverlay = hasOverlay && !!this.state.overlayGeoJson;
    const renderDissolvedOverlay =
      !!this.props.overlayDissolveKey && !!this.state.dissolvedOverlayGeoJson && renderOverlay;

    return (
      <div className="map-widget">
        <div className="map-wrapper">
          {this.props.showCustomLegend && <CustomLegend scale={this.props.customLegendScale} />}
          <BasisMap
            minZoom={this.props.minZoom}
            maxZoom={this.props.maxZoom}
            maxBounds={this.props.maxBounds}
            fitBounds={this.props.fitBounds || this.state.mapBounds}
            showBasemap={true}
            basemapUrl={this.props.basemapUrl}
            basemapAttribution={this.props.basemapAttribution}
            attributionPrefix={this.props.attributionPrefix}
            geocoderLatLng={this.state.geocoderLatLng}
            zoomControl={this.props.zoomControl}
            showMapToggleButton={this.props.showMapToggleButton}
            showLabels={this.props.showLabels}
            labelPreset={this.props.labelPreset}
            customLabels={this.props.customLabels}
            zoomDelta={this.props.zoomDelta}
            zoomSnap={this.props.zoomSnap}
            zoomAnimation={this.props.zoomAnimation}
            isFullScreen={this.props.isFullScreen}
            zoomOffset={this.props.zoomOffset}
            tileSize={this.props.tileSize}
            labelPreset={this.props.labelPreset}
            enableScroll={this.props.enableScroll}
          >
            {renderOverlay && (
              <Overlay
                data={this.state.overlayGeoJson}
                dissolveKey={this.props.overlayDissolveKey}
                className="mapwidget__overlay"
                smoothFactor={this.props.smoothFactor}
                fillColor="#F9F9F9"
                fillOpacity={1}
                fill
                color={this.props.overlayDissolveKey ? '#ccc' : '#D14F4F'}
                weight={0.5}
              />
            )}
            {renderDissolvedOverlay && (
              <Overlay
                data={this.state.dissolvedOverlayGeoJson}
                className="mapwidget__overlay"
                smoothFactor={this.props.smoothFactor}
                fillOpacity={0}
                color="#D14F4F"
                weight={0.5}
                opacity={1}
              />
            )}
            {renderMarkers && (
              <MarkerLayer
                data={this.props.data}
                onMouseEnter={this.onMouseEnter}
                onMouseMove={this.onMouseMove}
                onMouseOut={this.onMouseOut}
                maxValue={this.props.maxValue}
                minRadius={this.props.minRadius}
                maxRadius={this.props.maxRadius}
                date={this.props.date}
                maxValue={this.props.maxValue}
                confirmedFillScale={this.props.confirmedFillScale}
                showRecoveredCases={this.props.showRecoveredCases}
              />
            )}
            <GeocoderMarker position={this.state.geocoderLatLng} />
          </BasisMap>
          <Tooltip
            isVisible={this.state.tooltipVisible}
            pos={this.state.tooltipPos}
            data={this.state.tooltipData}
            onClose={this.onCloseTooltip}
            showRecoveredCases={this.props.showRecoveredCases}
            isMainMap={this.props.isMainMap}
          />
        </div>
        {this.props.showGeocoder && (
          <Geocoder
            title="Wie ist es bei Ihnen?"
            onSelect={this.onGeocoderSuccess}
            presets={this.props.searchPresets}
            presetsTitle={this.props.searchPresetsTitle}
            center={this.props.geocoderCenter}
            radius={this.props.geocoderRadius}
          />
        )}
      </div>
    );
  }
}

export default MapWrapper;
